/**
 * Cart Quantity Enforcer
 * Ensures no item can have quantity > 1 in the cart
 * Automatically removes duplicates and enforces single-item quantities
 */

(function () {
  "use strict";

  const CartQuantityEnforcer = {
    config: {
      debug: false,
      checkInterval: 500, // ms to wait after cart operations
    },

    /**
     * Initialize the enforcer
     */
    init() {
      this.log("Initializing Cart Quantity Enforcer");

      // Intercept add to cart form submissions
      this.interceptAddToCart();

      // Listen for cart updates
      this.listenForCartUpdates();

      // Run initial check
      this.enforceQuantityLimits();

      this.log("Cart Quantity Enforcer initialized");
    },

    /**
     * Intercept add to cart to check if item already exists
     */
    interceptAddToCart() {
      // Override the native fetch for cart/add requests
      const originalFetch = window.fetch;
      const self = this;

      window.fetch = function (url, options = {}) {
        // Get the URL string - handle both string URLs and Request objects
        let urlString = "";
        if (typeof url === "string") {
          urlString = url;
        } else if (url instanceof Request) {
          urlString = url.url;
        } else if (url && typeof url.toString === "function") {
          urlString = url.toString();
        }

        // Check if this is an add to cart request
        if (urlString && (urlString.includes("/cart/add") || urlString === routes?.cart_add_url)) {
          return self.handleAddToCart(originalFetch, url, options);
        }
        return originalFetch.apply(this, arguments);
      };
    },

    /**
     * Handle add to cart request - check if item already exists
     */
    async handleAddToCart(originalFetch, url, options) {
      try {
        // Get the variant ID being added
        let variantId = null;

        if (options.body instanceof FormData) {
          variantId = options.body.get("id");
        } else if (typeof options.body === "string") {
          try {
            const parsed = JSON.parse(options.body);
            variantId = parsed.id || (parsed.items && parsed.items[0]?.id);
          } catch (e) {
            // Not JSON, try URL encoded
            const params = new URLSearchParams(options.body);
            variantId = params.get("id");
          }
        }

        if (variantId) {
          // Check if this variant already exists in cart
          const cart = await this.fetchCart();
          const existingItem = cart.items.find((item) => item.variant_id.toString() === variantId.toString());

          if (existingItem) {
            this.log(`Item ${variantId} already in cart - blocking add`);

            // Return a mock response that indicates the item wasn't added
            // but doesn't trigger an error
            return new Response(
              JSON.stringify({
                items: cart.items,
                item_count: cart.item_count,
                message: "Item already in cart",
                already_in_cart: true,
              }),
              {
                status: 200,
                headers: { "Content-Type": "application/json" },
              }
            );
          }
        }
      } catch (error) {
        this.log("Error checking cart before add:", error);
        // Continue with the original request if check fails
      }

      // Proceed with original add request
      const response = await originalFetch(url, options);

      // After successful add, enforce quantity limits
      setTimeout(() => {
        this.enforceQuantityLimits();
      }, this.config.checkInterval);

      return response;
    },

    /**
     * Listen for cart update events
     */
    listenForCartUpdates() {
      // Listen for Shopify's cart update pub/sub events
      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          setTimeout(() => {
            this.enforceQuantityLimits();
          }, this.config.checkInterval);
        });
      }

      // Also listen for custom cart events
      document.addEventListener("cart:updated", () => {
        setTimeout(() => {
          this.enforceQuantityLimits();
        }, this.config.checkInterval);
      });

      // Listen for quantity input changes and cap at 1
      document.addEventListener("change", (e) => {
        if (e.target.matches('input[name="updates[]"], input.quantity__input, input[data-quantity-input]')) {
          const value = parseInt(e.target.value);
          if (value > 1) {
            e.target.value = 1;
            this.log("Quantity input capped to 1");

            // Trigger the change to update cart
            if (e.target.closest("cart-items, cart-drawer-items")) {
              const cartItems = e.target.closest("cart-items, cart-drawer-items");
              if (cartItems && cartItems.updateQuantity) {
                const index = e.target.dataset.index;
                cartItems.updateQuantity(index, 1);
              }
            }
          }
        }
      });
    },

    /**
     * Fetch current cart
     */
    async fetchCart() {
      const response = await fetch("/cart.js", {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      return response.json();
    },

    /**
     * Update cart quantities
     */
    async updateCart(updates) {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ updates }),
      });
      return response.json();
    },

    /**
     * Main enforcement logic - check cart and fix any violations
     */
    async enforceQuantityLimits() {
      try {
        const cart = await this.fetchCart();

        if (!cart.items || cart.items.length === 0) {
          return;
        }

        const updates = {};
        const itemsToFix = [];
        const seenVariants = new Set();

        cart.items.forEach((item, index) => {
          const variantId = item.variant_id.toString();

          // Check for duplicates (same variant appearing multiple times)
          if (seenVariants.has(variantId)) {
            // This is a duplicate - remove it entirely
            updates[variantId] = 0;
            itemsToFix.push({
              title: item.product_title,
              reason: "duplicate",
              action: "removed",
            });
            this.log(`Removing duplicate: ${item.product_title}`);
          } else {
            seenVariants.add(variantId);

            // Check if quantity > 1
            if (item.quantity > 1) {
              updates[variantId] = 1;
              itemsToFix.push({
                title: item.product_title,
                reason: "quantity exceeded",
                action: "reduced to 1",
              });
              this.log(`Reducing quantity to 1: ${item.product_title}`);
            }
          }
        });

        // Apply fixes if needed
        if (Object.keys(updates).length > 0) {
          await this.updateCart(updates);

          // Trigger cart UI refresh
          this.refreshCartUI();
        }
      } catch (error) {
        this.log("Error enforcing quantity limits:", error);
      }
    },

    /**
     * Refresh cart UI after changes
     */
    refreshCartUI() {
      // Dispatch cart update event for other components
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          bubbles: true,
          detail: { source: "quantity-enforcer" },
        })
      );

      // Publish to Shopify's pub/sub if available
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "quantity-enforcer",
        });
      }

      // Refresh cart drawer if open
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        fetch("/cart?section_id=cart-drawer")
          .then((response) => response.text())
          .then((html) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            const newContent = doc.querySelector("#CartDrawer");
            const currentDrawer = document.querySelector("#CartDrawer");
            if (newContent && currentDrawer) {
              currentDrawer.innerHTML = newContent.innerHTML;
            }
          });
      }

      // Refresh main cart page if on it
      if (window.location.pathname.includes("/cart")) {
        const cartItems = document.querySelector("cart-items");
        if (cartItems && cartItems.onCartUpdate) {
          cartItems.onCartUpdate();
        }
      }
    },

    /**
     * Debug logging
     */
    log(...args) {
      if (this.config.debug) {
        console.log("[CartQuantityEnforcer]", ...args);
      }
    },
  };

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => CartQuantityEnforcer.init());
  } else {
    CartQuantityEnforcer.init();
  }

  // Expose for debugging
  window.CartQuantityEnforcer = CartQuantityEnforcer;
})();
